import * as React from 'react';

import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';

import { OrDivider } from '@/components/common';
import { CycleCalendarSlider, CycleDurationSlider, LunarSwitch } from '@/components/cycle';
import { Button } from '@/components/ui';
import { DEFAULT_CYCLE_DURATION, DEFAULT_MENSTRUAL_PHASE_LENGTH } from '@/lib/constants';
import { useOnboardingStore } from '@/stores/useOnboardingStore';
import { getLastNewMoonDate } from '@/utils/date';

export const CycleOnboarding: React.FC = () => {
  const { replace, query } = useRouter();
  const fromCollective = query?.source === 'collective';
  const resetCycleData = useOnboardingStore((state) => state.resetCycleData);
  const setUserOnboardingSettings = useOnboardingStore((state) => state.setCycleData);
  const cycleData = useOnboardingStore((state) => state.cycleData);
  const lastNewMoon = React.useMemo(() => getLastNewMoonDate(), []);

  const {
    setValue,
    getValues,
    watch,
    formState: { isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: {
      cycleDuration: DEFAULT_CYCLE_DURATION,
      periodDuration: DEFAULT_MENSTRUAL_PHASE_LENGTH,
      cycleLunar: false,
      cycleStart: new Date(),
    },
  });

  const onSubmit = handleSubmit(({ cycleDuration, cycleLunar, cycleStart, periodDuration }) => {
    if (cycleData) {
      resetCycleData();
    }

    setUserOnboardingSettings({
      cycleDuration,
      cycleLunar,
      periodDuration,
      cycleStart: cycleStart.toISOString(),
    });

    replace(`/login${fromCollective ? '?source=collective&isNewUser=true' : ''}`);
  });

  const lunarEnabled = watch('cycleLunar');

  return (
    <form onSubmit={onSubmit}>
      <h1 className='text-2xl font-bodoni text-center tracking-[-0.9px] lg:text-4xl'>
        Please select the start and end date of your last period.
      </h1>
      <div className='text-center'>
        <p className='text-slate-1 mt-1'>Our technology uses your period to determine which phase you’re likely in.</p>
      </div>
      <CycleCalendarSlider
        defaultValue={getValues('cycleStart')}
        disabled={lunarEnabled}
        onChange={(date) => setValue('cycleStart', date)}
      />
      <CycleDurationSlider
        defaultValue={getValues('cycleDuration')}
        disabled={lunarEnabled}
        max={40}
        min={21}
        onChange={(duration) => setValue('cycleDuration', duration)}
        subtitle='Every woman is unique! Adjust to specify your typical cycle length (the number of days between your periods) to
        receive a more accurate experience.'
        title='Average Cycle Duration'
      />
      <CycleDurationSlider
        defaultValue={getValues('periodDuration')}
        disabled={lunarEnabled}
        max={7}
        min={3}
        onChange={(duration) => setValue('periodDuration', duration)}
        subtitle="What's your average period duration?"
        title='Average Period Length'
      />
      <OrDivider />
      <LunarSwitch
        enabled={lunarEnabled || false}
        lastNewMoon={lastNewMoon}
        setEnabled={(enabled) => setValue('cycleLunar', enabled)}
        showDescription={true}
      />
      <Button
        data-testid='period-calendar-submit-button'
        disabled={isSubmitting}
        state={isSubmitting ? 'waiting' : undefined}
        type='submit'
        variant='primary'
      >
        Continue
      </Button>
    </form>
  );
};
